import React from 'react';
import { Flame, Users, Timer, TrendingUp, XCircle } from 'lucide-react';

interface SocialProofBannerProps {
  stock: number;
  lowStockThreshold?: number;
  recentlyBought?: number;
  viewers?: number;
}

const SocialProofBanner: React.FC<SocialProofBannerProps> = ({
  stock,
  lowStockThreshold = 5,
  recentlyBought = 0,
  viewers = 0,
}) => {
  const displayViewers = viewers > 0 ? viewers : 0;

  const isLowStock = stock > 0 && stock <= lowStockThreshold;
  const isOutOfStock = stock <= 0;

  if (isOutOfStock) {
    return (
      <div className="bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-xl p-3 flex items-center gap-2 shadow-md">
        <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
        <span className="text-xs text-red-700 font-bold">Out of Stock</span>
      </div>
    );
  }

  return (
    <div className="space-y-2 overflow-hidden w-full min-w-0">
      {/* Stock urgency */}
      {isLowStock && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-3 flex items-center gap-2 animate-pulse overflow-hidden shadow-md">
          <Flame className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <span className="text-xs text-orange-800 font-bold break-words">
            Only {stock} left in stock — order soon!
          </span>
        </div>
      )}

      {/* Social proof row - only show if explicitly provided */}
      <div className="flex flex-wrap gap-2 overflow-hidden">
        {recentlyBought > 0 && (
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 px-3 py-2 rounded-lg flex-shrink-0 border border-blue-100/50 shadow-sm">
            <TrendingUp className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs text-blue-700 font-bold">
              {recentlyBought}+ bought in last 24 hours
            </span>
          </div>
        )}
        {viewers > 0 && (
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-gray-50 to-slate-50 px-3 py-2 rounded-lg flex-shrink-0 border border-gray-200/50 shadow-sm">
            <Users className="w-3.5 h-3.5 text-gray-600" />
            <span className="text-xs text-gray-600 font-bold">
              {displayViewers} viewing this now
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialProofBanner;
